import React, { useEffect, useState } from "react";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { getTenants } from "../../api/tenantApi";
import { transferEdge } from "../../api/capabilityApi";
import { apiErrorMessage } from "../../utils/capabilities";

// edge.transfer: move an edge to another tenant. Superadmin's request is applied at once ("approved");
// anyone else's waits for the target tenant's admin ("pending", see /edge-transfers).
export default function TransferEdgeDialog({ open, edge, accessToken, onClose, onTransferred }) {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [targetId, setTargetId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // transfer object: { id, status: "pending" | "approved", ... }

  useEffect(() => {
    if (!open) return;
    setTargetId("");
    setResult(null);
    setLoading(true);
    getTenants(accessToken, { limit: 200, offset: 0 })
      .then((data) => setTenants((data.items || data || []).filter((x) => x.id !== edge?.tenant_id)))
      .catch(() => setTenants([]))
      .finally(() => setLoading(false));
  }, [open, accessToken, edge]);

  const target = tenants.find((x) => x.id === targetId);

  const handleTransfer = async () => {
    if (!target) return;
    if (!window.confirm(t("edges.transfer.confirm", { edge: edge.name ?? edge.edgeId, tenant: target.name }))) return;
    setSubmitting(true);
    try {
      const res = await transferEdge(accessToken, edge.id, target.id);
      setResult(res);
      if (res.status === "approved") {
        toast.success(t("edges.transfer.done"));
        onTransferred?.();
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, t("edges.transfer.failed")));
    } finally {
      setSubmitting(false);
    }
  };

  const paper = { sx: { backgroundColor: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border)" } };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={paper}>
      <DialogTitle sx={{ borderBottom: "1px solid var(--border)" }}>{t("edges.transfer.title")}</DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {result ? (
          <Alert severity={result.status === "approved" ? "success" : "info"}>
            {result.status === "approved"
              ? t("edges.transfer.moved", { tenant: target?.name })
              : t("edges.transfer.pending", { id: result.id })}
          </Alert>
        ) : loading ? (
          <CircularProgress size={22} />
        ) : (
          <TextField
            select
            fullWidth
            size="small"
            label={t("edges.transfer.target")}
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            disabled={submitting}
          >
            {tenants.map((x) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)}
          </TextField>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid var(--border)", p: 2 }}>
        <Button onClick={onClose} sx={{ color: "var(--text-muted)" }}>{result ? t("common.close") : t("common.cancel")}</Button>
        {!result && (
          <Button variant="contained" onClick={handleTransfer} disabled={!target || submitting} sx={{ backgroundColor: "var(--btn-primary)" }}>
            {submitting ? t("edges.transfer.moving") : t("edges.move")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
